import { useCallback } from 'react';
import { API } from '../api';
import { usePaginatedList } from './usePaginatedList';

export const RECENT_EVENTS_PAGE_SIZE = 25;

// The contact filter is applied server-side so rows without a linked contact
// never eat into the page size and every page is a genuine 25.
const RECENT_EVENTS_QUERY = `
    query getCompanyEvents($companySlug: String!, $page: Int!) {
        events(
            filters: {
                type: { nei: "portal_login" }
                contact: { id: { notNull: true } }
                candidacy: {
                    job: { company: { slug: { eqi: $companySlug } } }
                }
            }
            sort: ["createdAt:desc"]
            pagination: { page: $page, pageSize: ${RECENT_EVENTS_PAGE_SIZE} }
        ) {
            data {
                id
                attributes {
                    type
                    createdAt
                    contact {
                        data {
                            id
                            attributes {
                                firstName
                                lastName
                            }
                        }
                    }
                }
            }
            meta {
                pagination {
                    page
                    pageCount
                    pageSize
                    total
                }
            }
        }
    }
`;

const mapEvent = (event) => ({
    id: event.id,
    type: event.attributes.type,
    createdAt: event.attributes.createdAt,
    contact: {
        id: event.attributes.contact.data.id,
        firstName: event.attributes.contact.data.attributes.firstName,
        lastName: event.attributes.contact.data.attributes.lastName,
    },
});

export const fetchRecentEvents = async (companySlug, page = 1) => {
    try {
        const response = await API.post('graphql', {
            query: RECENT_EVENTS_QUERY,
            variables: { companySlug, page },
        });
        const events = response.data.data?.events;
        return {
            items: (events?.data ?? []).map(mapEvent),
            pagination: events?.meta?.pagination ?? null,
        };
    } catch (error) {
        console.log(error);
        return { items: [], pagination: null };
    }
};

export const useRecentEventsInfinite = (companySlug) => {
    const fetchPage = useCallback(
        (page) => fetchRecentEvents(companySlug, page),
        [companySlug],
    );

    const { items, loadMore, loading, hasMore } = usePaginatedList({
        fetchPage,
        resetKey: companySlug,
    });

    return { allEvents: items, loadMore, loading, hasMore };
};
